/**
 * Basket tax display helpers for cart flyout and checkout pricing summaries.
 */

export const NET_TAX_SITE_IDS = new Set(['kl-us', 'kl-ca']);

/**
 * Whether the site uses address-dependent net tax (US / CA AvaTax).
 *
 * @param {string} [siteId]
 * @returns {boolean}
 */
export function isNetTaxSite(siteId) {
  return NET_TAX_SITE_IDS.has(siteId);
}

/**
 * Whether the basket shipment has enough address for AvaTax estimation.
 *
 * @param {Object} [basket]
 * @returns {boolean}
 */
export function hasBasketShippingAddress(basket) {
  return Boolean(basket?.shipments?.[0]?.shippingAddress?.postalCode);
}

/**
 * Resolve a basket tax amount, falling back to merchandize-level fields when
 * SCAPI leaves `taxTotal` null (common on gross sites before shipping is set).
 *
 * @param {Object} [basket]
 * @returns {number|null}
 */
export function resolveBasketTaxAmount(basket) {
  if (basket?.taxTotal != null) {
    return basket.taxTotal;
  }

  if (basket?.adjustedMerchandizeTotalTax != null) {
    return basket.adjustedMerchandizeTotalTax;
  }

  const groupedTaxValue = basket?.groupedTaxItems?.[0]?.taxValue;

  if (groupedTaxValue != null) {
    return groupedTaxValue;
  }

  return null;
}

/**
 * @typedef {'hidden' | 'amount' | 'placeholder'} BasketTaxDisplayKind
 */

/**
 * Decide how the tax row should render for a basket.
 *
 * US / CA (net): show "Not yet calculated" until a shipping address exists;
 * once an address exists, show $0 when SCAPI leaves tax null (e.g. AvaTax off).
 * Other sites (gross): show fallback amount when available; otherwise hide.
 *
 * @param {Object} [basket]
 * @param {string} [siteId]
 * @returns {{ kind: BasketTaxDisplayKind, amount?: number }}
 */
export function getBasketTaxDisplayState(basket, siteId) {
  const amount = resolveBasketTaxAmount(basket);

  if (isNetTaxSite(siteId) && !hasBasketShippingAddress(basket)) {
    return {kind: 'placeholder'};
  }

  if (amount != null) {
    return {kind: 'amount', amount};
  }

  if (isNetTaxSite(siteId) && hasBasketShippingAddress(basket)) {
    return {kind: 'amount', amount: 0};
  }

  return {kind: 'hidden'};
}

/**
 * Localized label for the tax row (gross vs net taxation).
 *
 * @param {import('react-intl').IntlShape} intl
 * @param {Object} [basket]
 * @returns {string}
 */
export function getBasketTaxLabel(intl, basket) {
  if (basket?.taxation === 'gross') {
    return intl.formatMessage({
      defaultMessage: 'Includes GST of',
      id: 'cart_pricing_summary.gst',
    });
  }

  return intl.formatMessage({
    defaultMessage: 'Tax',
    id: 'cart_pricing_summary.tax',
  });
}

/**
 * Localized placeholder when net-tax sites lack a shipping address.
 *
 * @param {import('react-intl').IntlShape} intl
 * @returns {string}
 */
export function getBasketTaxNotCalculatedLabel(intl) {
  return intl.formatMessage({
    defaultMessage: 'Not yet calculated',
    id: 'cart_pricing_summary.tax_not_calculated',
  });
}

/**
 * Whether the basket uses gross (tax-inclusive) pricing.
 *
 * @param {Object} [basket]
 * @returns {boolean}
 */
export function isGrossTaxBasket(basket) {
  return basket?.taxation === 'gross';
}

/**
 * Round a currency amount to two decimal places.
 *
 * @param {number} amount
 * @returns {number}
 */
export function roundCurrencyAmount(amount) {
  return Math.round(amount * 100) / 100;
}

/**
 * Infer the gross-tax rate embedded in an inclusive price.
 *
 * For gross pricing: tax = gross × rate / (1 + rate), so
 * rate = tax / (gross − tax).
 *
 * @param {number|null|undefined} grossAmount
 * @param {number|null|undefined} taxAmount
 * @returns {number|null}
 */
export function inferGrossTaxRate(grossAmount, taxAmount) {
  if (grossAmount == null || taxAmount == null) {
    return null;
  }

  const netAmount = grossAmount - taxAmount;

  if (netAmount <= 0) {
    return null;
  }

  return taxAmount / netAmount;
}

/**
 * Extract gross tax from an inclusive gross base using a known rate.
 *
 * @param {number|null|undefined} grossBase
 * @param {number|null|undefined} taxRate
 * @returns {number|null}
 */
export function extractGrossTaxFromInclusiveBase(grossBase, taxRate) {
  if (grossBase == null || taxRate == null || taxRate < 0) {
    return null;
  }

  return roundCurrencyAmount((grossBase * taxRate) / (1 + taxRate));
}

/**
 * @typedef {Object} BasketPricingSummaryTotals
 * @property {number} shippingAmount
 * @property {{ kind: BasketTaxDisplayKind, amount?: number }} taxDisplay
 * @property {number} orderTotal
 */

/**
 * Resolve cart / checkout pricing-summary totals, optionally estimating gross
 * tax and shipping before a shipping method is selected on the basket.
 *
 * Gross sites (non US/CA): when `estimatedShipping` is provided and no method
 * is selected, derive the embedded tax rate from product-only basket tax and
 * apply it to `(productTotal + estimatedShipping)` so the GST row and total
 * match checkout once the cheapest shipping option is chosen.
 *
 * Net sites (US / CA): before a shipping method is selected, SCAPI's
 * `orderTotal` is often items-only and omits estimated shipping and AvaTax.
 * Recompute `productTotal + estimatedShipping + tax` instead of trusting it.
 *
 * @param {Object} params
 * @param {Object} [params.basket]
 * @param {string} [params.siteId]
 * @param {number|null} [params.estimatedShipping]
 * @param {boolean} [params.hasSelectedShipping]
 * @returns {BasketPricingSummaryTotals}
 */
export function resolveBasketPricingSummaryTotals({
  basket,
  siteId,
  estimatedShipping = null,
  hasSelectedShipping = false,
}) {
  const productTotal = basket?.productTotal ?? 0;
  const shippingAmount = hasSelectedShipping ?
    (basket?.shippingTotal ?? 0) :
    (estimatedShipping ?? 0);

  const shouldEstimateGrossTax = isGrossTaxBasket(basket) &&
    !hasSelectedShipping &&
    estimatedShipping != null;

  if (shouldEstimateGrossTax) {
    const productTax = resolveBasketTaxAmount(basket);
    const taxRate = inferGrossTaxRate(productTotal, productTax);
    const grossBase = productTotal + shippingAmount;
    const estimatedTax = taxRate != null ?
      extractGrossTaxFromInclusiveBase(grossBase, taxRate) :
      null;

    const taxDisplay = estimatedTax != null ?
      {kind: 'amount', amount: estimatedTax} :
      getBasketTaxDisplayState(basket, siteId);

    return {
      shippingAmount,
      taxDisplay,
      orderTotal: grossBase,
    };
  }

  const taxDisplay = getBasketTaxDisplayState(basket, siteId);
  const taxAmount = taxDisplay.kind === 'amount' ? (taxDisplay.amount ?? 0) : 0;

  // US / CA: SCAPI orderTotal is frequently stale (items-only) until the
  // shopper picks a shipping method, even when AvaTax has already returned a
  // tax amount from geolocation. Always recompute the displayed total here.
  if (isNetTaxSite(siteId) && !hasSelectedShipping) {
    return {
      shippingAmount,
      taxDisplay,
      orderTotal: roundCurrencyAmount(productTotal + shippingAmount + taxAmount),
    };
  }

  if (basket?.orderTotal != null) {
    return {
      shippingAmount,
      taxDisplay,
      orderTotal: basket.orderTotal,
    };
  }

  if (isNetTaxSite(siteId)) {
    return {
      shippingAmount,
      taxDisplay,
      orderTotal: roundCurrencyAmount(productTotal + shippingAmount + taxAmount),
    };
  }

  return {
    shippingAmount,
    taxDisplay,
    orderTotal: productTotal + shippingAmount,
  };
}
